"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TabDragController = void 0;
const bounds_observer_1 = require("../utils/bounds-observer");
/**
 * Set of apis used to facilitate tab drag interactions without needing to hide views.
 * @ignore
 */
class TabDragController {
    // eslint-disable-next-line
    constructor(viewOverlay) {
        this.viewOverlay = viewOverlay;
        /**
         *
         * When a tab is dragged out of a stack, it will need to be hidden from the stack.
         *
         * Additionally, if there is a new view to show in the stack, it will be shown at the position specified by
         * containerBounds
         *
         * As drag interactions can under extreme circumstances complete before this chain of promises has completed,
         * we need to pass in a isDragging() function which returns whether the drag is in progress.
         * This allows us to cancel any layout affecting operations.
         *
         * @param draggingView The view which is currently being dragged
         * @param isLastViewInWindow Whether the draggin view is the last view in a window or not. If false, the dragging view will not hide.
         * @param isDragging A function which returns true if the drag is still in progress. As we chain some async calls here, we want to avoid
         * modifying any views if the drag has completed (as the post drag procedure will have taken care of it.)
         * @param containerBounds The bounds of the container of the view to be shown in the stack
         * @param nextView The view which has become active after dragging the draggingView out.
         */
        this.handleTabStackActiveView = async (draggingView, isLastViewInWindow, isDragging, containerBounds, nextView) => {
            if (this.dropZonePreview) {
                if (nextView && containerBounds) {
                    if (isDragging()) {
                        await (nextView === null || nextView === void 0 ? void 0 : nextView.show());
                    }
                    if (isDragging()) {
                        await (nextView === null || nextView === void 0 ? void 0 : nextView.setBounds(containerBounds));
                    }
                }
                if (isDragging() && !isLastViewInWindow) {
                    await draggingView.hide();
                }
            }
        };
        /**
         * Extracts the border and backgroundColor css values from the drop zone preview,
         * and sets the viewOverlay to match them.
         */
        this.inheritStyles = async () => {
            if (this.dropZonePreview) {
                const { border, backgroundColor } = getComputedStyle(this.dropZonePreview);
                await this.viewOverlay.setStyle({ border, backgroundColor });
            }
        };
        /**
         * Called when a tab drag interaction is started from the current window (not when it enters the window).
         *
         * Sets all views in the platform to ignore mouse events so that they can pass through to the golden-layout
         * document whilst remaining visible.
         */
        this.startDrag = async () => {
            await this.viewOverlay.setIgnoreViewMouseEvents(true);
        };
        /**
         * Called when a tab drag interaction which was started from the current window ends.
         *
         * Disables the click through setting on every view in the platform.
         */
        this.endDrag = async () => {
            await this.viewOverlay.setIgnoreViewMouseEvents(false);
        };
        this.disposeOverlayObserver = () => {
            if (this.disposeObserve) {
                this.disposeObserve();
            }
            this.dropZonePreview = undefined;
        };
        /**
         * Observes a golden-layout drop zone preview in order to render a BrowserView
         * overlay whenever a tab is dragged over a droppable region.
         * @param dropZonePreview The drop zone preview element created by Golden Layout in order to highlight
         * droppable regions of the UI.
         */
        this.observeOverlay = async (dropZonePreview) => {
            if (!this.dropZonePreview) {
                this.dropZonePreview = dropZonePreview;
                let lastBounds;
                dropZonePreview.style.visibility = 'hidden';
                const onDropAreaHighlighted = async (e) => {
                    try {
                        const { bounds } = e.detail;
                        if (!lastBounds || !(0, bounds_observer_1.isDomRectEqual)(lastBounds, bounds)) {
                            lastBounds = bounds;
                            await this.viewOverlay.renderOverlay(bounds);
                        }
                    }
                    catch (error) {
                        console.warn('Unexpected error encountered rendering tab drag preview.', error);
                    }
                };
                const onDropAreaHidden = async () => {
                    try {
                        lastBounds = undefined;
                        await this.viewOverlay.detachOverlay();
                    }
                    catch (error) {
                        console.warn('Unexpected error encountered hiding tab drag preview.', error);
                    }
                };
                dropZonePreview.addEventListener('drop-area-highlighted', onDropAreaHighlighted);
                dropZonePreview.addEventListener('drop-area-hidden', onDropAreaHidden);
                this.disposeObserve = () => {
                    dropZonePreview.removeEventListener('drop-area-highlighted', onDropAreaHighlighted);
                    dropZonePreview.removeEventListener('drop-area-hidden', onDropAreaHidden);
                };
            }
            else {
                console.warn('Tried to observe a drop zone overlay without disposing the previous.');
            }
        };
    }
}
exports.TabDragController = TabDragController;
